import { createReducer } from '../../store.model';
import { User } from '../../user/user.model';
import { Scorecard } from '../scorecard-next.model';
import {
    createEmpty,
    createEmptyBox,
    createEmptyData,
    datasetKey,
} from '../scorecard.model';
import { selectSCFromTab, setTabData } from '../scorecard.operators';

import { determineDefaultDirectReportGrid } from './determine-default-direct-report-grid.function';
import { determineDirectReportGrids } from './determine-direct-report-grids.function';
import { getLogoUrlFromTemplateConfig } from './get-logo-from-template-config.function';
import { GridTab } from './grid-tab.model';
import {
    ActionType,
    CreateTab,
    CreateTabResult,
    Export,
    ExportResult,
    ListData,
    ListDataResult,
    Reload,
    ReloadResult,
    RemoveTab,
    Reset,
    ResetGrid,
    SelectedDirectReportOption,
    SelectedGridTab,
    SelectPeriodType,
    SelectScorecard,
    SelectTab,
} from './scorecard.action';
import { defaultState, ScorecardState } from './scorecard.state';

const { reducer, builder } = createReducer<ActionType, ScorecardState>(
    defaultState(),
);

/* TODO can maybe consolidate repetitive lines once are things stable */

builder
    .add<CreateTab>(ActionType.SCORECARD__CREATE_TAB, (state, action) => {
        let user = action.payload.user;
        if (!user) {
            user = new User();
            user.id = action.payload.userId;
            user.firstName = '...';
            user.lastName = '';
        }

        let tab = Array.from(state.tabs.values()).find(
            (e) => e.userId === user.id,
        );
        let nextTabs = state.tabs;

        if (!tab) {
            // create tab
            tab = {
                tabId: action.payload.tabId,
                userId: user.id,
                user,
                pending: {},
                displayOrder: state.tabs.size + 1, // TODO remember to rewrite for all tabs when tab move/remove occurs
                scorecardHistory: new Map(),
                scorecardDataset: new Map(),
                defaultSelectedSCId: action.payload.defaultSelectedSCId,
            };
            // state.tabs.set(tab.tabId, tab);

            // next tab set
            nextTabs = new Map(state.tabs);
            nextTabs.set(tab.tabId, tab);
        }

        return {
            ...state,
            tabs: nextTabs, // TODO revise
            selectedTab: tab,
            authUserId: action.payload.authUserId,
        };
    })
    .add<CreateTabResult>(
        ActionType.SCORECARD__CREATE_TAB_RESULT,
        (state, action) => {
            if (!action.payload.user) {
                return state;
            }

            let tab = state.tabs.get(action.payload.tabId);

            // recreate tab
            tab = {
                ...tab,
                user: action.payload.user,
            };

            // next tab set
            const nextTabs = new Map(state.tabs);
            nextTabs.set(tab.tabId, tab);

            return {
                ...state,
                tabs: nextTabs,
                selectedTab:
                    tab.tabId === state.selectedTab.tabId
                        ? tab
                        : state.selectedTab,
            };
        },
    )
    .add<RemoveTab>(ActionType.SCORECARD__REMOVE_TAB, (state, action) => {
        const nextTabs = new Map(state.tabs);
        nextTabs.delete(action.payload.tabId);

        let nextOrder = 1;
        // eslint-disable-next-line no-return-assign
        nextTabs.forEach((x) => (x.displayOrder = nextOrder++));

        let nextSelectedTabId = state.selectedTab.tabId;
        if (action.payload.tabId === state.selectedTab.tabId) {
            const tabsKeys = Array.from(state.tabs.keys());
            const indexCurrentTab = tabsKeys.indexOf(action.payload.tabId);
            nextSelectedTabId =
                tabsKeys[
                    indexCurrentTab +
                        (tabsKeys.length - 1 > indexCurrentTab ? 1 : -1)
                ];
        }
        const nextSelectedTab = nextTabs.get(nextSelectedTabId);

        return {
            ...state,
            tabs: nextTabs,
            selectedTab: nextSelectedTab,
        };
    })
    .add<SelectTab>(ActionType.SCORECARD__SELECT_TAB, (state, action) => {
        const tab = state.tabs.get(action.payload.tabId);

        return {
            ...state,
            selectedTab: tab,
        };
    });

builder
    .add<Reload>(ActionType.SCORECARD__RELOAD, (state, action) => {
        let tab = state.tabs.get(action.payload.tabId);
        const nextUseLiveData =
            action.payload.useLiveData != null
                ? action.payload.useLiveData
                : tab.useLiveData;
        const toggleUseLiveData = tab.useLiveData !== nextUseLiveData;

        // recreate tab
        tab = {
            ...tab,
            pending: {
                ...tab.pending,
                reload: true,
                reloadStartTime: Date.now(),
            },
            useLiveData: nextUseLiveData,
        };
        // state.tabs.set(tab.tabId, tab);

        if (toggleUseLiveData || action.payload.hard) {
            // almost equivalent to a new tab
            tab.rootConfig = undefined;
            tab.periodTypes = undefined;
            tab.scorecardHistory = new Map();
            tab.scorecardDataset = new Map();

            // equivalent to no scorecard being selected
            tab.selectedPeriodType = undefined;
            tab.selectedScorecardHistory = undefined;
            tab.selectedScorecard = undefined;
            tab.selectedGridTab = undefined;
            tab.directReportGrids = undefined;
            tab.selectedDirectReportDropdownItem = undefined;
        }

        // next tab set
        const nextTabs = new Map(state.tabs);
        nextTabs.set(tab.tabId, tab);

        return {
            ...state,
            tabs: nextTabs,
            selectedTab:
                tab.tabId === state.selectedTab.tabId ? tab : state.selectedTab,
        };
    })
    .add<ReloadResult>(ActionType.SCORECARD__RELOAD_RESULT, (state, action) => {
        let tab = state.tabs.get(action.payload.tabId);
        const boxes = action.payload.scorecardBoxes || [createEmptyBox()];

        // recreate tab
        tab = {
            ...tab,
            pending: {
                ...tab.pending,
                reload: false,
                reloadStartTime: undefined,
            },
            scorecardHistory: new Map(tab.scorecardHistory),
            scorecardDataset: new Map(tab.scorecardDataset),
        };

        boxes.forEach((box) => {
            tab = setTabData(
                tab,
                box,
                tab.scorecardHistory,
                tab.scorecardDataset,
            );
        });

        if (tab.defaultSelectedSCId != null) {
            const nextSc = selectSCFromTab(tab, tab.defaultSelectedSCId);

            // header could be 'empty'. only set if found
            if (nextSc.header.scorecardId === tab.defaultSelectedSCId) {
                tab.selectedScorecard = nextSc;
                tab.selectedGridTab =
                    tab.selectedGridTab || GridTab.SCORECARD_GRID;
                tab.directReportGrids = determineDirectReportGrids(
                    nextSc.data.templateConfig,
                );
                tab.logoUrl = getLogoUrlFromTemplateConfig(
                    nextSc.data.templateConfig,
                );
                tab.selectedDirectReportDropdownItem =
                    determineDefaultDirectReportGrid(
                        tab.directReportGrids,
                        tab.selectedDirectReportDropdownItem,
                    );
            }

            /* this will only be true if defaultSelectedSCId === initialScorecard.
            clear defaultSelectedSCId since data is already present */
            if (nextSc.data.scorecardId === tab.defaultSelectedSCId) {
                tab.defaultSelectedSCId = null;
            }
        }

        // next tab set
        const nextTabs = new Map(state.tabs);
        nextTabs.set(tab.tabId, tab);

        return {
            ...state,
            tabs: nextTabs,
            selectedTab:
                tab.tabId === state.selectedTab.tabId ? tab : state.selectedTab,
        };
    });

builder
    .add<ListData>(ActionType.SCORECARD__LIST_DATA, (state, action) => {
        let tab = state.tabs.get(action.payload.tabId);

        // recreate tab
        tab = {
            ...tab,
            pending: {
                ...tab.pending,
                listData: true,
            },
        };
        state.tabs.set(tab.tabId, tab);

        return {
            ...state,
            selectedTab:
                tab.tabId === state.selectedTab.tabId ? tab : state.selectedTab,
        };
    })
    .add<ListDataResult>(
        ActionType.SCORECARD__LIST_DATA_RESULT,
        (state, action) => {
            let tab = state.tabs.get(action.payload.tabId);

            // next dataset
            const dats = Array.from(tab.scorecardHistory.values()).reduce(
                (p, c) => [...p, ...c],
                [],
            );
            const nextScs = new Map(tab.scorecardDataset);
            action.payload.scorecardData.forEach((e) => {
                const sc = dats.find((ee) => ee.scorecardId === e.scorecardId);
                const key = datasetKey(sc.periodType, e.scorecardId);
                nextScs.set(key, e);
            });

            // recreate tab
            tab = {
                ...tab,
                pending: {
                    ...tab.pending,
                    listData: false,
                },
                scorecardDataset: nextScs,
            };
            if (tab.defaultSelectedSCId != null) {
                const nextSc = selectSCFromTab(tab, tab.defaultSelectedSCId);

                // header could be 'empty'. only set if found
                if (nextSc.header.scorecardId === tab.defaultSelectedSCId) {
                    tab.selectedScorecard = nextSc;
                    tab.selectedGridTab =
                        tab.selectedGridTab || GridTab.SCORECARD_GRID;
                    tab.directReportGrids = determineDirectReportGrids(
                        nextSc.data.templateConfig,
                    );
                    tab.selectedDirectReportDropdownItem =
                        determineDefaultDirectReportGrid(
                            tab.directReportGrids,
                            tab.selectedDirectReportDropdownItem,
                        );
                }

                tab.defaultSelectedSCId = null;
            }
            state.tabs.set(tab.tabId, tab);

            return {
                ...state,
                selectedTab:
                    tab.tabId === state.selectedTab.tabId
                        ? tab
                        : state.selectedTab,
            };
        },
    );

builder.add<SelectPeriodType>(
    ActionType.SCORECARD__SELECT_PERIOD_TYPE,
    (state, action) => {
        let tab = state.tabs.get(action.payload.tabId);

        // next period type
        const nextPt = tab.periodTypes.find(
            (e) => e.periodTypeId === action.payload.periodType,
        );

        // next scorecard history
        const nextSh = tab.scorecardHistory.get(nextPt.periodTypeId);

        // next scorecard
        // TODO is this sorted? maybe tab should have 'defaultScorecardId' or just save the last-selected-scorecard for each period type
        const head = nextSh[nextSh.length - 1];

        const key = datasetKey(nextPt.periodTypeId, head.scorecardId);
        const nextSc: Scorecard = {
            header: head || createEmpty(),
            data: tab.scorecardDataset.get(key) || createEmptyData(),
        };

        const nextGrids = determineDirectReportGrids(
            nextSc.data.templateConfig,
        );
        const nextDefaultGrid = determineDefaultDirectReportGrid(
            nextGrids,
            tab.selectedDirectReportDropdownItem,
        );

        // recreate tab
        tab = {
            ...tab,
            selectedPeriodType: nextPt,
            selectedScorecardHistory: nextSh,
            selectedScorecard: nextSc,
            selectedGridTab: tab.selectedGridTab || GridTab.SCORECARD_GRID,
            directReportGrids: nextGrids,
            selectedDirectReportDropdownItem: nextDefaultGrid,
        };
        state.tabs.set(tab.tabId, tab);

        return {
            ...state,
            selectedTab:
                tab.tabId === state.selectedTab.tabId ? tab : state.selectedTab,
        };
    },
);

builder.add<SelectScorecard>(ActionType.SCORECARD__SELECT, (state, action) => {
    let tab = state.selectedTab;

    const nextSc = selectSCFromTab(tab, action.payload.scorecardId);
    const nextGrids = determineDirectReportGrids(nextSc.data.templateConfig);
    const nextDefaultGrid = determineDefaultDirectReportGrid(
        nextGrids,
        tab.selectedDirectReportDropdownItem,
    );

    // recreate tab
    tab = {
        ...tab,
        selectedScorecard: nextSc,
        selectedGridTab: tab.selectedGridTab || GridTab.SCORECARD_GRID,
        directReportGrids: nextGrids,
        selectedDirectReportDropdownItem: nextDefaultGrid,
    };
    state.tabs.set(tab.tabId, tab);

    return {
        ...state,
        selectedTab: tab,
    };
});

builder
    .add<Export>(ActionType.SCORECARD__EXPORT, (state, action) => {
        let tab = state.selectedTab;

        // recreate tab
        tab = {
            ...tab,
            pending: {
                ...tab.pending,
                export: true,
            },
        };
        state.tabs.set(tab.tabId, tab);

        return {
            ...state,
            selectedTab: tab,
        };
    })
    .add<ExportResult>(ActionType.SCORECARD__EXPORT_RESULT, (state, action) => {
        let tab = state.selectedTab;

        // recreate tab
        tab = {
            ...tab,
            pending: {
                ...tab.pending,
                export: false,
            },
        };
        state.tabs.set(tab.tabId, tab);

        return {
            ...state,
            selectedTab: tab,
        };
    });

builder.add<Reset>(ActionType.SCORECARD__RESET, (state, action) => {
    return defaultState();
});

builder.add<ResetGrid>(ActionType.SCORECARD__RESET_GRID, (state, action) => {
    return {
        ...state,
    };
});

builder.add<SelectedGridTab>(
    ActionType.SCORECARD__SELECT_GRID_TAB,
    (state, action) => {
        let tab = state.selectedTab;

        // recreate tab
        tab = {
            ...tab,
            selectedGridTab: action.payload.selectedGridTab,
        };
        state.tabs.set(tab.tabId, tab);

        return {
            ...state,
            selectedTab: tab,
        };
    },
);

builder.add<SelectedDirectReportOption>(
    ActionType.SCORECARD__SELECT_DIRECT_REPORT_DROPDOWN,
    (state, action) => {
        let tab = state.selectedTab;

        // recreate tab
        tab = {
            ...tab,
            selectedDirectReportDropdownItem:
                action.payload.selectedDirectReportOption,
        };
        state.tabs.set(tab.tabId, tab);

        return {
            ...state,
            selectedTab: tab,
        };
    },
);

export default {
    scorecard: {
        root: reducer,
    },
};
